import axios from 'axios';
import { getDeviceToken, setDeviceToken, getDeviceFingerprint } from '../utils/deviceAuth';

// For DEV const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// For PROD
const API_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token and device token
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add device token if available
    const deviceToken = getDeviceToken();
    if (deviceToken) {
      config.headers['X-Device-Token'] = deviceToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Request interceptor: add cache-busting timestamp to all GET requests
api.interceptors.request.use((config) => {
  if (config.method === 'get') {
    config.params = { ...config.params, _t: Date.now() };
  }
  return config;
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear auth data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  checkRegistrationAllowed: async () => {
    const response = await api.get('/registration-allowed');
    return response.data;
  },

  register: async (name, username, email, password, passwordConfirmation) => {
    const response = await api.post('/register', {
      name,
      username,
      email,
      password,
      password_confirmation: passwordConfirmation,
    });
    return response.data;
  },

  login: async (username, password) => {
    const deviceToken = getDeviceToken();
    const deviceFingerprint = getDeviceFingerprint();

    const response = await api.post('/login', {
      username,
      password,
      device_token: deviceToken,
      device_fingerprint: deviceFingerprint,
    });

    const { token, user, device } = response.data;

    // Only store auth token if device verification is not required
    if (token) {
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      // Clear any existing auth data if device verification is required
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    }

    // Store device token
    if (device.device_token) {
      setDeviceToken(device.device_token);
    }

    return response.data;
  },

  logout: async () => {
    try {
      await api.post('/logout');
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    }
  },

  getMe: async () => {
    const response = await api.get('/me');
    return response.data;
  },

  verifyDevice: async (verificationToken) => {
    const response = await api.post('/verify-device', {
      verification_token: verificationToken,
    });
    return response.data;
  },

  resendVerification: async () => {
    const deviceToken = getDeviceToken();
    const response = await api.post('/resend-verification', {
      device_token: deviceToken,
    });
    return response.data;
  },

  checkDeviceStatus: async () => {
    const deviceToken = getDeviceToken();
    const response = await api.post('/check-device-status', {
      device_token: deviceToken,
    });
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirmation: newPassword,
    });
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/forgot-password', {
      email,
    });
    return response.data;
  },

  resetPassword: async (token, email, password, passwordConfirmation) => {
    const response = await api.post('/reset-password', {
      token,
      email,
      password,
      password_confirmation: passwordConfirmation,
    });
    return response.data;
  },
};

// Customer API calls
export const customerAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/customers', { params });
    return response.data;
  },

  search: async (searchTerm) => {
    const response = await api.get('/customers', { 
      params: { 
        search: searchTerm,
        per_page: 50 
      } 
    });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/customers/${id}`);
    return response.data;
  },

  create: async (customerData) => {
    const response = await api.post('/customers', customerData);
    return response.data;
  },

  update: async (id, customerData) => {
    const response = await api.put(`/customers/${id}`, customerData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/customers/${id}`);
    return response.data;
  },
};

// Customer Detail Field API calls
export const customerDetailAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/customer-details', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/customer-details/${id}`);
    return response.data;
  },

  create: async (detailData) => {
    const response = await api.post('/customer-details', detailData);
    return response.data;
  },

  update: async (id, detailData) => {
    const response = await api.put(`/customer-details/${id}`, detailData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/customer-details/${id}`);
    return response.data;
  },
};

// Admin API calls
export const adminAPI = {
  getDashboardStats: async () => {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  },

  getUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  getUser: async (id) => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },

  updateUserRole: async (id, roleId) => {
    const response = await api.patch(`/admin/users/${id}/role`, {
      role_id: roleId,
    });
    return response.data;
  },

  updateUserStatus: async (id, isActive) => {
    const response = await api.patch(`/admin/users/${id}/status`, {
      is_active: isActive,
    });
    return response.data;
  },

  getRoles: async () => {
    const response = await api.get('/admin/roles');
    return response.data;
  },

  createUser: async (userData) => {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },

  getAuthLogs: async (params = {}) => {
    const response = await api.get('/admin/logs/auth', { params });
    return response.data;
  },

  getLogDates: async () => {
    const response = await api.get('/admin/logs/dates');
    return response.data;
  },

  // Dashboard statistics
  getOrderStats: async () => {
    const response = await api.get('/dashboard/order-stats');
    return response.data;
  },

  getDailyOrderStats: async () => {
    const response = await api.get('/dashboard/daily-stats');
    return response.data;
  },

  // Database backup API calls
  getBackups: async () => {
    const response = await api.get('/admin/backups');
    return response.data;
  },

  createBackup: async (notes = null) => {
    const response = await api.post('/admin/backups', { notes });
    return response.data;
  },

  getBackup: async (id) => {
    const response = await api.get(`/admin/backups/${id}`);
    return response.data;
  },

  restoreBackup: async (id) => {
    const response = await api.post(`/admin/backups/${id}/restore`);
    return response.data;
  },

  deleteBackup: async (id) => {
    const response = await api.delete(`/admin/backups/${id}`);
    return response.data;
  },

  verifyBackup: async (id) => {
    const response = await api.get(`/admin/backups/${id}/verify`);
    return response.data;
  },

  downloadBackup: async (id) => {
    const response = await api.get(`/admin/backups/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  uploadBackup: async (file, notes = null) => {
    const formData = new FormData();
    formData.append('backup_file', file);
    if (notes) {
      formData.append('notes', notes);
    }
    const response = await api.post('/admin/backups/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Product API calls
export const productAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/products', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  create: async (productData) => {
    const response = await api.post('/products', productData);
    return response.data;
  },

  update: async (id, productData) => {
    const response = await api.put(`/products/${id}`, productData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  },
};

// Order API calls
export const orderAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/orders', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/orders/${id}`);
    return response.data;
  },

  create: async (orderData) => {
    const response = await api.post('/orders', orderData);
    return response.data;
  },

  update: async (id, orderData) => {
    const response = await api.put(`/orders/${id}`, orderData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/orders/${id}`);
    return response.data;
  },

  getByCustomer: async (customerId) => {
    const response = await api.get(`/orders/customer/${customerId}`);
    return response.data;
  },

  togglePayment: async (id) => {
    const response = await api.patch(`/orders/${id}/toggle-payment`);
    return response.data;
  },

  getByScheduleDate: async (date) => {
    const response = await api.get(`/orders/by-schedule-date/${date}`);
    return response.data;
  },

  getUnallocatedByScheduleDate: async (date) => {
    const response = await api.get(`/orders/by-schedule-date/${date}/unallocated`);
    return response.data;
  },

  /**
   * The orders in a date range that still have unallocated lines.
   *
   * The Diary shows a RANGE of dates. Asking for each day separately
   * would mean one request per day - sixty-odd for a two-month range.
   * Give both dates for a bounded range, or neither for everything.
   */
  getUnallocatedInRange: async (startDate, endDate) => {
    const params = {};
    if (startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    }
    const response = await api.get('/orders/unallocated', { params });
    return response.data;
  },

  searchOrders: async (params) => {
    const response = await api.get('/orders', { params });
    return response.data;
  },

  batchReschedule: async (orderIds, scheduleDate, options = {}) => {
    const response = await api.post('/orders/batch-reschedule', {
      order_ids: orderIds,
      schedule_date: scheduleDate,
      remove_delivery_items: options.removeDeliveryItems ?? true,
    });
    return response.data;
  },

  splitOrderLine: async (lineId, splitData) => {
    const response = await api.post(`/orders/lines/${lineId}/split`, splitData);
    return response.data;
  },
};

// Delivery Run API calls
export const deliveryRunAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/delivery-runs', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/delivery-runs/${id}`);
    return response.data;
  },

  create: async (runData) => {
    const response = await api.post('/delivery-runs', runData);
    return response.data;
  },

  update: async (id, runData) => {
    const response = await api.put(`/delivery-runs/${id}`, runData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/delivery-runs/${id}`);
    return response.data;
  },

  getByDate: async (date) => {
    const response = await api.get('/delivery-runs', { params: { delivery_date: date } });
    return response.data;
  },

  addDelivery: async (runId, deliveryData) => {
    const response = await api.post(`/delivery-runs/${runId}/add-delivery`, deliveryData);
    return response.data;
  },

  removeDelivery: async (runId, deliveryId, deliveryItemId = null) => {
    const config = {};
    if (deliveryItemId) {
      config.data = { delivery_item_id: deliveryItemId };
    }
    const response = await api.delete(`/delivery-runs/${runId}/deliveries/${deliveryId}`, config);
    return response.data;
  },

  updateDeliveryPosition: async (runId, deliveryId, position, deliveryItemId = null) => {
    const data = { loading_position: position };
    if (deliveryItemId) {
      data.delivery_item_id = deliveryItemId;
    }
    const response = await api.patch(`/delivery-runs/${runId}/deliveries/${deliveryId}/position`, data);
    return response.data;
  },

  cancel: async (id, action, rescheduleDate = null) => {
    const data = { action };
    if (action === 'reschedule' && rescheduleDate) {
      data.reschedule_date = rescheduleDate;
    }
    const response = await api.post(`/delivery-runs/${id}/cancel`, data);
    return response.data;
  },

  checkAvailability: async (date, time) => {
    const response = await api.get('/delivery-runs/check-availability', {
      params: { delivery_date: date, start_time: time }
    });
    return response.data;
  },
};

// Vehicle Availability Exception API calls
export const vehicleAvailabilityAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/vehicle-availability-exceptions', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/vehicle-availability-exceptions/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/vehicle-availability-exceptions', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/vehicle-availability-exceptions/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/vehicle-availability-exceptions/${id}`);
    return response.data;
  },
};

// Webhook API calls
export const webhookAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/webhooks', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/webhooks/shopify/${id}/status`);
    return response.data;
  },

  reset: async (id) => {
    const response = await api.post(`/webhooks/shopify/${id}/reset`);
    return response.data;
  },
};

export default api;
